import styled from "styled-components";
import { getTheme, themed, Text as Tx, } from "@bracket/ui";
import type { ColorDeclaration, ThemePalette, } from "@bracket/ui/types";


import {
	Fragment,
	type ButtonHTMLAttributes,
	type CSSProperties,
	type FunctionComponent,
	type ReactNode,
	type SyntheticEvent,
} from "react";

import * as ICONS from "@bracket/ui/icons";
import { StyledButton } from "./StyledButton";
import { Loader, Typography, } from "@bracket/ui/components";
import { ButtonLabel } from "./ButtonLabel";

import type { Size, Variant } from "./types";
import { Div, } from "@bracket/ui/nodes";

export declare type ButtonProps = {
	onClick?: ( evt: SyntheticEvent<HTMLButtonElement> ) => void;
	label: string | ReactNode;
	inner?: FunctionComponent<any>;
	innerProps?: Record<string, any>;
	size?: Size;
	variant?: Variant;
	disabled?: boolean;
	icon?: keyof typeof ICONS;
	iconPosition?: "start" | "end";
	iconProps?: any;
	/** indicate a background process is running by rendering a spinner */
	busy?: boolean;
	busyText?: string;
	mt?: string;
	mb?: string;
	ml?: string;
	mr?: string;
	className?: string;
	width?: string;
	maxWidth?: string;
	flexible?: boolean;
	style?: CSSProperties;
	id?: string;
	color?: string;
	type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
	weight?: string;
	interactive?: boolean;
	/** if true, the button will invest the colors */
	inverse?: boolean;
	innerColor?: ColorDeclaration | keyof ThemePalette;
};




/**
 * A versatile button component that supports multiple variants, sizes, icons and loading states.
 * Can render custom inner content and handles disabled states.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|ReactNode} props.label - Text or content to display in the button
 * @param {() => void} [props.onClick] - Click handler function
 * @param {FunctionComponent} [props.inner] - Custom component to render button content
 * @param {string} [props.icon] - Icon name from ICONS to display
 * @param {Object} [props.innerProps={}] - Props passed to inner component
 * @param {Size} [props.size="md"] - Button size variant
 * @param {Variant} [props.variant="primary"] - Button style variant
 * @param {boolean} [props.disabled=false] - WhetheColorDeclarationr button is disabled
 * @param {string} [props.mt="0"] - Margin top
 * @param {string} [props.mb="0"] - Margin bottom
 * @param {string} [props.ml="0"] - Margin left
 * @param {string} [props.mr="0"] - Margin right
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {string} [props.width] - Custom width
 * @param {string} [props.maxWidth]Div - Maximum width
 * @param {boolean} [props.busy=false] - Show loading spinner
 * @param {string} [props.busyText=""] - Text to show while loading
 * @param {"start"|"end"} [props.iconPosition="start"] - Icon placement
 * @param {Object} [props.iconProps={}] - Props passed to icon component
 * @param {boolean} [props.flexible] - Allow button to grow/shrink
 * @param {CSSProperties} [props.style={}] - Additional CSS styles
 * @param {"button"|"submit"|"reset"} [props.type="button"] - HTML button type
 * @returns {JSX.Element} A button component
 */
export const Button: FunctionComponent<ButtonProps> = ({
	label,
	onClick = ( ) => {},
	inner,
	icon,
	innerProps = {},
	size = "md",
	variant = "primary",
	disabled = false,
	mt = "0",
	mb = "0",
	ml = "0",
	mr = "0",
	className = "",
	width,
	maxWidth,
	busy = false,
	busyText = "",
	iconPosition = "start",
	iconProps = {},
	flexible,
	style = {},
	type = "button",
	color,
	innerColor,
	weight,
	interactive = true,
	inverse = false,
	...props
}) => {
	themed();

	const Inner = inner || ButtonLabel;
	const IconComponent = ((icon && ICONS[icon]) || Fragment) as FunctionComponent;

	return (
		<StyledButton
			onClick={!disabled ? onClick : undefined}
			$size={size}
			$variant={variant}
			disabled={disabled}
			$busy={busy}
			$mt={mt}
			$mb={mb}
			$ml={ml}
			$mr={mr}
			$width={width}
			$maxWidth={maxWidth}
			$flexible={flexible}
			style={style}
			className={className}
			type={type}
			$color={color}
			$innerColor={innerColor}
			$interactive={interactive}
			{...props}
		>
			{ busy ? (
				<Brace $inverse={inverse} $innerColor={innerColor}>
				<Loader type="ellipsis" color={innerColor} center />
					{ busyText && ( <BusyText><Tx>{busyText}</Tx></BusyText> ) }
				</Brace>
			) : (
				<>
					{icon && iconPosition === "start" && <IconComponent {...iconProps} />}
					<Inner label={label} size={size} disabled={disabled} weight={weight} {...innerProps} />
					{icon && iconPosition === "end" && <IconComponent {...iconProps} />}
				</>
			)}
		</StyledButton>
	);
};


const Brace = styled(Div)<{ $color?: string; $innerColor?: ColorDeclaration | keyof ThemePalette; $inverse?: boolean; }>`
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
	background-color: ${({ $color }) => $color || "transparent"};
	color: ${({ $innerColor, }) => $innerColor ? getTheme()[$innerColor as keyof ThemePalette] : $innerColor || getTheme().surface_on};
	height: 100%;
`

const BusyText = styled(Div)`
	position: absolute;
	top: 100px;
	> p {
		color: white;
		opacity: 0.5;
		white-space: nowrap;
	}
`;
